import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { TFunction } from 'i18next';
import find from 'lodash/find';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { PortalContext } from '../../context/PortalContext';
import { useCoverageType } from '../../hooks/useCoverageType';
import { useLagoon } from '../../hooks/useLagoon';
import { usePrimaryCare } from '../../hooks/usePrimaryCare';
import { BreadcrumbSessionStorage } from '../../models/BreadcrumbSessionStorage';
import { ProviderDetails } from '../../models/ProviderDetails';
import { useChipStore } from '../../store/useChipStore';
import { usePCPStore } from '../../store/usePCPStore';
import { PCPSearchStore } from '../../store/usePCPStore/pcpStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { PcpOutputConfig } from '../../utils/pcpConfigUtil';
import {
  doChoosePCPStoreUpdate,
  isCnsRestrictedEditPcp,
} from '../../utils/primaryCare.utils';
import {
  getCurrentMember,
  getLoggedInMember,
  getPolicyNumber,
  getReportingCode,
} from '../../utils/user.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { MaximumProviderLimitBanner } from '../BookmarkButton/MaximumProviderLimitBanner';
import {
  ANALYTICS_CONSTANTS,
  isInvalidCurrentPcpID,
  isInvalidFuturePcpID,
} from '../CareTeamCard/CareTeamUtils';
import { Constants, PESNetworkStatus, ProviderTypes } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { ConstantsRoutes } from '../ConstantsRoutes';
import {
  HeaderContentWrapper,
  HeaderFlex,
} from '../DetailsPageHeader/DetailsPageHeader.styled';
import { Breadcrumb } from '../PSXHeader/Breadcrumb';
import { PrimaryCare } from '../PSXHeader/ObapiDemo/memberProfile';
import { ThingsToConsiderModal } from '../ThingsToConsiderModal';
import { getFeatureFlag } from '../Utils';
import { isEniMember, isUmrMember } from '../Utils/memberUtils/memberUtils';
import {
  HeaderContainer,
  HeaderPageWrapper,
  careTeamHeaderStyle,
} from './CareTeamHeader.styled';

interface LagoonBannerMessage {
  message: string;
  title?: string;
  url?: string;
}

interface PcpAlertBannerProps {
  pcpProvider: ProviderDetails | null;
  pcpInfo: PrimaryCare | null;
  t: TFunction;
  choosePcpButtonText: string | undefined;
  onChoosePcp: () => void;
  isVisible: boolean;
  onClose: () => void;
  suppressedBanner?: LagoonBannerMessage;
  oonBanner?: LagoonBannerMessage;
  futurePcpSuppressedBanner?: LagoonBannerMessage;
  futurePcpOonBanner?: LagoonBannerMessage;
  filteredPcpConfig: PcpOutputConfig | null;
  isFuturePcp?: boolean;
  lockInIndicator?: boolean;
  isCnsRestricted?: boolean;
  currentMember?: any;
}

const getPcpAlertBanner = ({
  pcpProvider,
  pcpInfo,
  t,
  choosePcpButtonText,
  onChoosePcp,
  isVisible,
  onClose,
  suppressedBanner,
  oonBanner,
  futurePcpSuppressedBanner,
  futurePcpOonBanner,
  filteredPcpConfig,
  isFuturePcp = false,
  lockInIndicator = false,
  isCnsRestricted = false,
  currentMember,
}: PcpAlertBannerProps) => {
  if (!isVisible) return null;
  const providerName =
    pcpProvider?.providerName ||
    [pcpProvider?.firstName, pcpProvider?.middleName, pcpProvider?.lastName]
      .filter(Boolean)
      .join(' ');

  const providerLocations = pcpProvider?.providerLocations;
  const networkStatus = providerLocations?.[0]?.network?.status;
  const pcpName = providerName || pcpInfo?.pcpName;
  const pcpProviderId = pcpInfo?.providerID;

  if (networkStatus === PESNetworkStatus.SUPPRESSED) {
    const bannerMessage = isFuturePcp
      ? futurePcpSuppressedBanner
      : suppressedBanner;
    const suppressedTitle = bannerMessage?.message?.replace(
      '{providerName}',
      pcpName || ''
    );

    return (
      <Alert
        children={''}
        data-testid="care-team-header-suppressed-alert"
        dismissible={true}
        isVisible={true}
        onClose={onClose}
        status="warning"
        title={suppressedTitle}
      />
    );
  }
  // If providerId exists but provider data hasn't loaded yet, wait for data
  if (pcpProviderId && !pcpProvider) {
    return null;
  }
  // For staff model PCPs, wait for care team provider data before deciding
  if (pcpInfo?.isStaffModel && !pcpProvider) {
    return null;
  }
  // Staff model PCPs may not have providerID in primary care response;
  // use actual care team provider locations to determine network status
  const isStaffModelInNetwork =
    pcpInfo?.isStaffModel &&
    providerLocations?.some((loc) => loc?.network?.status === 'INN');

  if (isStaffModelInNetwork) {
    return null;
  }
  // Show OON banner if: no providerId OR provider is OON/NOT_FOUND
  if (
    !pcpProviderId ||
    !providerLocations ||
    providerLocations.length === 0 ||
    networkStatus === 'OON' ||
    networkStatus === 'NOT_FOUND'
  ) {
    const bannerConfig = isFuturePcp ? futurePcpOonBanner : oonBanner;
    const bannerTitle = bannerConfig?.title?.replace(
      '{providerName}',
      pcpName || ''
    );

    const isCnsIndianaMember =
      currentMember?.isCnSMember && (lockInIndicator || isCnsRestricted);
    const shouldHideFuturePcpCta =
      isFuturePcp && filteredPcpConfig?.showCurrentPcpCard;
    const shouldHideCta =
      (filteredPcpConfig && shouldHideFuturePcpCta) || isCnsIndianaMember;

    return (
      <Alert
        cta={
          shouldHideCta
            ? undefined
            : {
                type: 'button',
                props: {
                  children: choosePcpButtonText || t('Choose PCP'),
                  onClick: onChoosePcp,
                },
              }
        }
        data-testid="care-team-header-oon-alert"
        dismissible={true}
        isVisible={true}
        onClose={onClose}
        status="warning"
        title={bannerTitle}
      >
        {bannerConfig?.message}
      </Alert>
    );
  }
  return null;
};

/**
 * Selects the appropriate PCP unavailable alert message based on PCP validity flags.
 */
function selectPcpUnavailableAlert(
  hasInvalidPcpID: boolean,
  hasInvalidFuturePcpID: boolean,
  currentFuturePcpErrorMessage: any,
  currentPcpErrorMessage: any,
  futurePcpErrorMessage: any,
  pcpErrorMessage: any
) {
  if (hasInvalidPcpID && hasInvalidFuturePcpID)
    return currentFuturePcpErrorMessage;
  if (hasInvalidPcpID) return currentPcpErrorMessage;
  if (hasInvalidFuturePcpID) return futurePcpErrorMessage;
  return pcpErrorMessage;
}

export const CareTeamHeader = ({
  pcpProvider,
  futurePcpProvider,
  chipValue,
  pageNumber,
  ENABLE_NEW_PCP_CARE_TEAM_FLOW,
  filteredPcpConfig,
}) => {
  const { t } = useTranslation();
  const lagoonData = useLagoon(Constants.LAGOON_TABLE.UI_MESSAGING)();
  const [visibleAlerts, setVisibleAlerts] = useState([true, true]);
  const [pcpBannerVisible, setPcpBannerVisible] = useState(true);
  const [futurePcpBannerVisible, setFuturePcpBannerVisible] = useState(true);
  const [errorAlertVisible, setErrorAlertVisible] = useState(true);
  const { portalData } = useContext(PortalContext);
  const [showModal, setShowModal] = useState(false);
  const [showPCPBanner, setShowPCPBanner] = useState(false);

  const { navigate } = useRouter();

  const [breadcrumbSessionStorage, setBreadcrumbSessionStorage] =
    useSessionStorage<BreadcrumbSessionStorage>(
      Constants.STORAGE_KEYS.SESSION.BREADCRUMB_URLS,
      {}
    );

  useEffect(() => {
    if (breadcrumbSessionStorage[ConstantsRoutes.CARE_TEAM.key]) {
      delete breadcrumbSessionStorage[ConstantsRoutes.CARE_TEAM.key];
    }
    setBreadcrumbSessionStorage(breadcrumbSessionStorage);
    clearPCPSearchState();
  }, []);

  const parseBreadcrumbs = () => {
    const breadcrumbs = [
      {
        title: t('CARE_TEAM.CARE_TEAM_BREADCRUMB'),
        href: '',
      },
    ];
    return breadcrumbs;
  };
  const breadcrumbs = parseBreadcrumbs();

  useEffect(() => {
    document.title = `${t('CARE_TEAM.CARE_TEAM_BREADCRUMB')} | ${
      Constants.SITE_NAME
    }`;
  }, []);

  const headerDescription = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.CARE_TEAM.HEADER_DESCRIPTION,
  });

  const { data } = usePrimaryCare();
  const { primaryCare, hasPCPFetchError } = data || {};

  const choosePcpButtonMessage = find(lagoonData, {
    key: ConstantsLagoon.PRIMARY_CARE.CHOOSE_PCP_BUTTON,
  })?.message;

  const featureFlags = useLagoon('feature-flags')();
  const allConfigs = useLagoon('config')();
  const loggedInMemberInfo = getLoggedInMember();
  const { demographics, lineOfBusiness } = loggedInMemberInfo || {};
  const currentMember = getCurrentMember();
  const isUmr = isUmrMember(portalData?.portalName);

  const displayThingsToConsiderForEni = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_THINGS_TO_CONSIDER_MODAL
  );
  const showThingsToConsider = () =>
    !isUmr && isEniMember(lineOfBusiness) && displayThingsToConsiderForEni;

  const chipStore = useChipStore();
  const { setPCPSearchState, clearPCPSearchState } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      setPCPSearchState: state.setPCPSearchState,
      clearPCPSearchState: state.clearPCPSearchState,
    }))
  );
  const { setTypeaheadSearchStore } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
    }))
  );

  const openThingsToConsiderModal = () => setShowModal(true);
  const closeThingsToConsiderModal = () => setShowModal(false);

  const onAddPCPClick = () => {
    adobeLinkTrackEvent({
      destinationUrl: '',
      name: 'choose-pcp',
      location: `body:${ANALYTICS_CONSTANTS.PCP_SNACK_CARD}`,
      type: 'internal',
    });
    const urlParams = doChoosePCPStoreUpdate(
      demographics?.dependentSeqNbr,
      chipStore,
      setTypeaheadSearchStore,
      setPCPSearchState
    );
    navigate(`${ConstantsRoutes.PROVIDER_SEARCH_RESULTS.path}${urlParams}`);
  };

  const handleChoosePcp = () => {
    if (showThingsToConsider()) {
      openThingsToConsiderModal();
    } else {
      onAddPCPClick();
    }
  };

  const handleContinue = () => {
    setShowModal(false);
    onAddPCPClick();
  };

  const dependentSeqNbr = loggedInMemberInfo?.demographics?.dependentSeqNbr;
  const pcpInfo = primaryCare?.find(
    ({ depSeqNbr }) => dependentSeqNbr === depSeqNbr
  )?.pcpInfo;
  const hasInvalidPcpID = isInvalidCurrentPcpID(pcpInfo);
  const hasInvalidFuturePcpID = isInvalidFuturePcpID(pcpInfo);
  useEffect(() => {
    if (chipValue !== 'MEDICAL_SPECIALISTS' || pageNumber !== 1) {
      setShowPCPBanner(false);
      return;
    }
    const providerLocations = pcpProvider?.providerLocations;
    const networkStatus = providerLocations?.[0]?.network?.status;
    // Staff model PCPs may not have providerID in primary care response;
    // use actual care team provider locations to determine network status
    const isStaffModelInNetwork =
      pcpInfo?.isStaffModel &&
      providerLocations?.some((loc) => loc?.network?.status === 'INN');
    const suppressed =
      (!pcpInfo?.providerID && !pcpInfo?.isStaffModel) ||
      networkStatus === PESNetworkStatus.SUPPRESSED;
    const oon =
      !providerLocations ||
      providerLocations?.length === 0 ||
      networkStatus === 'OON' ||
      networkStatus === 'NOT_FOUND';

    const { isKelseySeyboldPlan, isSanitasPlan } = currentMember;
    const isKelseySeyboldPlanMember =
      isKelseySeyboldPlan &&
      pcpInfo?.providerType?.toLowerCase() ===
        ProviderTypes.MEDICAL_GROUP.toLowerCase();

    const sanitasPCP = allConfigs?.find(
      (row) => row.key === 'SANITAS_PCPID'
    )?.value;

    const isSanitasPlanMember = isSanitasPlan && pcpInfo?.pcpID === sanitasPCP;

    const isExternalPlanMember =
      isKelseySeyboldPlanMember || isSanitasPlanMember;

    if (isExternalPlanMember || isStaffModelInNetwork) setShowPCPBanner(false);
    else if ((suppressed || oon) && pcpProvider && pcpInfo?.hasPCP) {
      setShowPCPBanner(true);
    } else {
      setShowPCPBanner(false);
    }
  }, [pcpInfo?.providerID, pcpProvider, pcpInfo?.hasPCP, futurePcpProvider]);

  const viewIdCardHeader = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.VIEW_ID_CARD_HEADER,
  });
  const viewIdCardContent = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.VIEW_ID_CARD_CONTENT,
  });
  const viewIdCardButton = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.VIEW_ID_CARD_BUTTON,
  });
  const suppressedBanner = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.CARE_TEAM
      .CARE_TEAM_BANNER_FOR_SUPPRESSED_PCP,
  });
  const oonBanner = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.CARE_TEAM.CARE_TEAM_BANNER_FOR_OON_PCP,
  });
  const futurePcpSuppressedBanner = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.CARE_TEAM
      .CARE_TEAM_BANNER_FOR_SUPPRESSED_FUTURE_PCP,
  });
  const futurePcpOonBanner = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.CARE_TEAM
      .CARE_TEAM_BANNER_FOR_OON_FUTURE_PCP,
  });
  const pcpErrorMessage = find(lagoonData, {
    key: ConstantsLagoon.PRIMARY_CARE.PCP_ERROR_MESSAGE,
  });
  const currentPcpErrorMessage = find(lagoonData, {
    key: ConstantsLagoon.PRIMARY_CARE.CURRENT_PCP_ERROR_MESSAGE,
  });
  const futurePcpErrorMessage = find(lagoonData, {
    key: ConstantsLagoon.PRIMARY_CARE.FUTURE_PCP_ERROR_MESSAGE,
  });
  const currentFuturePcpErrorMessage = find(lagoonData, {
    key: ConstantsLagoon.PRIMARY_CARE.CURRENT_FUTURE_PCP_ERROR_MESSAGE,
  });

  const lockInIndicator = primaryCare?.[0]?.lockInIndicator;

  const handleViewIdCard = () => {
    window.location.href =
      '/community/dashboard/modal/id-cards?goBackOnClose=1';
  };

  const customMesssges = useLagoon('custom-messages')();
  const coverageType = useCoverageType();
  const memberPolicyNumber = getPolicyNumber(currentMember, coverageType);
  const memberReportingCode = getReportingCode(currentMember, coverageType);

  const isCnsReportingCode = isCnsRestrictedEditPcp(
    customMesssges,
    memberReportingCode,
    memberPolicyNumber
  );
  const pcpUnavailableAlert = selectPcpUnavailableAlert(
    hasInvalidPcpID,
    hasInvalidFuturePcpID,
    currentFuturePcpErrorMessage,
    currentPcpErrorMessage,
    futurePcpErrorMessage,
    pcpErrorMessage
  );

  return (
    <div>
      <HeaderPageWrapper>
        <HeaderContainer>
          <HeaderContentWrapper>
            <HeaderFlex alignItems="center" justify="space-between">
              <Breadcrumb
                backgroundColor="$bgProviderDetailsColor"
                breadcrumbs={breadcrumbs}
                onBackButtonClick={() => {
                  navigate(-1);
                }}
                showBreadcrumbAtMediumScreenSize={true}
              />
            </HeaderFlex>
            <Heading
              css={careTeamHeaderStyle}
              data-auto-testid="care-team-header"
              data-testid="care-team-header"
              level={1}
              size="xl"
            >
              {t('CARE_TEAM.CARE_TEAM_BREADCRUMB')}
            </Heading>
          </HeaderContentWrapper>

          <Text
            data-auto-testid="care-team-header-description"
            data-testid="care-team-header-description"
            fontSize="lg"
          >
            {headerDescription?.message}
          </Text>
          {showModal && (
            <ThingsToConsiderModal
              editPcpClicked={false}
              isOpen={showModal}
              onClose={closeThingsToConsiderModal}
              onContinue={handleContinue}
            />
          )}
        </HeaderContainer>
      </HeaderPageWrapper>
      {showPCPBanner &&
      !hasInvalidPcpID &&
      (filteredPcpConfig?.showCurrentPcpCard || !ENABLE_NEW_PCP_CARE_TEAM_FLOW)
        ? getPcpAlertBanner({
            pcpProvider,
            pcpInfo,
            t,
            choosePcpButtonText: choosePcpButtonMessage,
            onChoosePcp: handleChoosePcp,
            isVisible: pcpBannerVisible,
            onClose: () => setPcpBannerVisible(false),
            suppressedBanner,
            oonBanner,
            filteredPcpConfig,
            isFuturePcp: false,
            lockInIndicator,
            isCnsRestricted: isCnsReportingCode,
            currentMember,
          })
        : null}
      {pcpInfo?.futurePCP &&
      !hasInvalidFuturePcpID &&
      filteredPcpConfig?.showFuturePcpCard
        ? getPcpAlertBanner({
            pcpProvider: futurePcpProvider,
            pcpInfo: pcpInfo?.futurePCP,
            t,
            choosePcpButtonText: choosePcpButtonMessage,
            onChoosePcp: handleChoosePcp,
            isVisible: futurePcpBannerVisible,
            onClose: () => setFuturePcpBannerVisible(false),
            futurePcpSuppressedBanner,
            futurePcpOonBanner,
            filteredPcpConfig,
            isFuturePcp: true,
            lockInIndicator,
            isCnsRestricted: isCnsReportingCode,
            currentMember,
          })
        : null}
      {(() => {
        let shouldShowAlert = false;

        if (!ENABLE_NEW_PCP_CARE_TEAM_FLOW) {
          // Legacy behavior: Show alert if lockInIndicator or isCnsReportingCode
          shouldShowAlert = lockInIndicator || isCnsReportingCode;
        } else {
          // New config-driven behavior
          const {
            showCurrentPcpCard,
            showCurrentPcpViewCard,
            showFuturePcpCard,
            showFuturePcpViewCard,
          } = filteredPcpConfig ?? new PcpOutputConfig();

          const shouldShowForCurrentPcp =
            showCurrentPcpCard && showCurrentPcpViewCard;
          const shouldShowForFuturePcp =
            showFuturePcpCard && showFuturePcpViewCard;

          shouldShowAlert =
            (lockInIndicator || isCnsReportingCode) &&
            (shouldShowForCurrentPcp || shouldShowForFuturePcp);
        }

        return shouldShowAlert ? (
          <Alert
            cta={{
              type: 'link',
              props: {
                children: viewIdCardButton?.message,
                href: '#/community/dashboard/modal/id-cards?goBackOnClose=1',
                onClick: handleViewIdCard,
              },
            }}
            data-testid="care-team-header-view-id-card-alert"
            dismissible={true}
            isVisible={visibleAlerts[1]}
            onClose={() =>
              setVisibleAlerts((prev) => {
                const newAlerts = [...prev];
                newAlerts[1] = false;
                return newAlerts;
              })
            }
            status="info"
            title={viewIdCardHeader?.message}
          >
            {viewIdCardContent?.message}
          </Alert>
        ) : null;
      })()}
      <MaximumProviderLimitBanner isCareTeamView={true} />
      {(hasPCPFetchError || hasInvalidPcpID || hasInvalidFuturePcpID) &&
        ENABLE_NEW_PCP_CARE_TEAM_FLOW &&
        (filteredPcpConfig?.showCurrentPcpCard ||
          filteredPcpConfig?.showFuturePcpCard) && (
          <Alert
            data-testid="pcp-error-alert"
            dismissible={true}
            isVisible={errorAlertVisible}
            onClose={() => setErrorAlertVisible(false)}
            status="info"
            title={pcpUnavailableAlert?.title}
          >
            {pcpUnavailableAlert?.message}
          </Alert>
        )}
    </div>
  );
};
